import React from 'react';
import debounce from 'debounce-promise';
import Select from 'react-select';
import {
  Alert,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner
} from 'reactstrap';
import { ModalConfirmation } from '../../../../components/Modals';
import { COLLECTION_ROUTE_API } from '../../../../constants';
import { logout } from '../../../../actions/authAction';
import { apiClient } from '../../../../utils/Api';
import { checkAuthorized, manipulateErrors } from '../../../../utils';
import store from '../../../../store';
import {
  buildInitialState,
  buildStagedSummary,
  countMeaningfulCsvRows,
  dedupeStagedVariants,
  extractCsvEntries,
  MAX_STAGED_VARIANTS,
  mapVariantToOption,
  mergeValidatedVariants,
  MODE_BULK_ADD,
  MODE_REPLACE_ALL,
  normalizeSku
} from './bulkProductVariantModalHelpers';

const HIDDEN_INPUT_STYLE = { display: 'none' };
const CSV_BTN_STYLE = { background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginLeft: 8 };
const SCROLLABLE_LIST_STYLE = { maxHeight: '150px', overflowY: 'auto' };
const SMALL_TEXT_STYLE = { fontSize: '12px' };
const SELECTED_BOX_STYLE = { backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', padding: '12px', borderRadius: '4px' };

class ModalBulkProductVariant extends React.Component {
  constructor(props) {
    super(props);
    this.state = buildInitialState();
    this.csvInputRef = React.createRef();
    this._isMounted = false;
    this._debouncedLoadVariantOptions = debounce(this._fetchVariantOptions, 300);
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen !== prevProps.isOpen) {
      this._resetModalState();
    }
  }

  _handleRequestError = (error) => {
    if (!checkAuthorized(manipulateErrors(error))) {
      store.dispatch(logout());
    }
  }

  _resetModalState = (callback) => {
    this.setState(buildInitialState(), callback);
  }

  _fetchVariantOptions = async (input) => {
    if (!input || !input.trim()) {
      return { options: [] };
    }

    try {
      const response = await apiClient().get(`${COLLECTION_ROUTE_API}/${this.props.collectionId}/product_variants`, {
        params: {
          exclude: true,
          keyword: input,
          per_page: 15,
          page: 1,
          sort_by: 'updatedAt_desc'
        }
      });

      const items = response.data?.data?.data || [];
      return {
        options: items.map(mapVariantToOption)
      };
    } catch (error) {
      this._handleRequestError(error);

      return { options: [] };
    }
  }

  _loadVariantOptions = (input) => {
    return this._debouncedLoadVariantOptions(input);
  }

  _triggerCsvInput = () => {
    if (!this.csvInputRef.current) return;

    this.csvInputRef.current.value = '';
    this.csvInputRef.current.click();
  }

  _handleModeChange = (e) => {
    this.setState({ mode: e.target.value });
  }

  _handleStageChange = (selected) => {
    const stagedVariants = dedupeStagedVariants(selected);
    if (stagedVariants.length > MAX_STAGED_VARIANTS) {
      this.setState({
        selectionError: `Maximum ${MAX_STAGED_VARIANTS} SKUs can be staged at one time.`
      });
      return;
    }

    this.setState({
      stagedVariants,
      csvSuccessMessage: null,
      selectionError: null
    });
  }

  _readFileAsText = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = event => resolve(event.target.result);
    reader.onerror = event => reject(event);
    reader.readAsText(file);
  });

  _handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (extension !== '.csv') {
      this.setState({
        csvError: 'Please upload a .csv file.',
        csvValidationResults: null,
        showCsvResults: false,
        csvSuccessMessage: null
      });
      return;
    }

    this._parseAndValidateCsv(file);
  }

  _parseAndValidateCsv = async (file) => {
    this.setState({
      csvParsing: true,
      csvValidating: false,
      csvValidationCount: 0,
      csvValidationResults: null,
      showCsvResults: false,
      csvError: null,
      csvSuccessMessage: null,
      selectionError: null
    });

    try {
      const content = await this._readFileAsText(file);
      if (!this._isMounted) return;

      const rows = content.split(/\r?\n/);
      const meaningfulRowCount = countMeaningfulCsvRows(rows);
      if (meaningfulRowCount > MAX_STAGED_VARIANTS) {
        this.setState({
          csvParsing: false,
          csvError: `File exceeds maximum of ${MAX_STAGED_VARIANTS} SKU rows.`
        });
        return;
      }

      const { invalid, unresolvedEntries } = extractCsvEntries(rows, this.state.stagedVariants);
      if (this.state.stagedVariants.length + unresolvedEntries.length > MAX_STAGED_VARIANTS) {
        this.setState({
          csvParsing: false,
          csvError: `Maximum ${MAX_STAGED_VARIANTS} SKUs can be staged at one time.`
        });
        return;
      }

      if (unresolvedEntries.length === 0) {
        this.setState({
          csvParsing: false,
          csvValidating: false,
          csvValidationResults: { valid: [], alreadyExists: [], invalid },
          showCsvResults: true
        });
        return;
      }

      this.setState({
        csvParsing: false,
        csvValidating: true,
        csvValidationCount: unresolvedEntries.length
      });

      const resolved = await this._validateSkusAgainstApi(unresolvedEntries);
      if (!this._isMounted) return;

      this.setState({
        csvValidating: false,
        csvValidationResults: {
          valid: resolved.valid,
          alreadyExists: resolved.alreadyExists,
          invalid: [...invalid, ...resolved.invalid]
        },
        showCsvResults: true
      });
    } catch (error) {
      this._handleRequestError(error);

      if (!this._isMounted) return;

      this.setState({
        csvParsing: false,
        csvValidating: false,
        csvError: 'Failed to process CSV file. Please try again.'
      });
    }
  }

  _validateSkusAgainstApi = async (entries) => {
    const response = await apiClient().post(`${COLLECTION_ROUTE_API}/${this.props.collectionId}/product_variants/bulk`, {
      skus: entries.map(item => item.sku)
    });

    const result = response.data?.data || {};
    const matchedItems = result.matched?.items || [];
    const alreadyExistsItems = result.already_exists?.items || [];
    const notFoundItems = result.not_found?.items || [];

    return {
      valid: matchedItems.map(mapVariantToOption),
      alreadyExists: alreadyExistsItems.map(item => ({ ...mapVariantToOption(item), alreadyExists: true })),
      invalid: notFoundItems.map(item => ({
        sku: item.SKU,
        reason: 'SKU not found through current Product Variants search source'
      }))
    };
  }

  _confirmAddCsvResults = () => {
    const { csvValidationResults, stagedVariants } = this.state;
    if (!csvValidationResults) return;

    const merged = mergeValidatedVariants(stagedVariants, csvValidationResults);
    if (merged.length > MAX_STAGED_VARIANTS) {
      this.setState({
        selectionError: `Maximum ${MAX_STAGED_VARIANTS} SKUs can be staged at one time.`
      });
      return;
    }

    const addedCount = merged.length - stagedVariants.length;

    this.setState({
      stagedVariants: merged,
      csvValidationResults: null,
      showCsvResults: false,
      selectionError: null,
      csvSuccessMessage: addedCount > 0
        ? `Added ${addedCount} SKU${addedCount > 1 ? 's' : ''} into selection.`
        : 'No new SKUs were added into selection.'
    });
  }

  _clearCsvResults = () => {
    this.setState({
      csvValidationResults: null,
      showCsvResults: false,
      csvError: null,
      selectionError: null
    });
  }

  _getSummary = () => {
    return buildStagedSummary(this.state.stagedVariants, this.state.mode);
  }

  _handleRemoveSelectedSku = (skuToRemove) => {
    this.setState({
      stagedVariants: this.state.stagedVariants.filter(item => normalizeSku(item.sku) !== normalizeSku(skuToRemove)),
      csvSuccessMessage: null,
      selectionError: null
    });
  }

  _renderCsvReviewSection = (title, color, items, renderItem) => {
    if (!items.length) return null;

    return (
      <Alert color={color} className="mt-2 mb-0">
        <strong>{title} ({items.length})</strong>
        <div className="mt-2" style={SCROLLABLE_LIST_STYLE}>
          <ul className="mb-0 pl-3" style={SMALL_TEXT_STYLE}>
            {items.map(renderItem)}
          </ul>
        </div>
      </Alert>
    );
  }

  _submitBulkAdd = async () => {
    const summary = this._getSummary();
    if (summary.valid.length === 0) return;

    this.setState({ isSubmitting: true, submitError: null });

    try {
      await apiClient().post(`${COLLECTION_ROUTE_API}/${this.props.collectionId}/product_variants`, {
        product_variants: summary.valid.map(item => item.id)
      });

      if (!this._isMounted) return;

      this.setState({ isSubmitting: false });
      this.props.onSuccess();
      this.props.toggle();
    } catch (error) {
      this._handleRequestError(error);

      if (!this._isMounted) return;

      this.setState({
        isSubmitting: false,
        submitError: 'Failed to bulk add product variants.'
      });
    }
  }

  _submitReplaceAll = async () => {
    const summary = this._getSummary();
    if (summary.submitEligible.length === 0) return;

    this.setState({
      isSubmitting: true,
      submitError: null,
      isReplaceConfirmationOpen: false
    });

    try {
      await apiClient().put(`${COLLECTION_ROUTE_API}/${this.props.collectionId}/product_variants/replace`, {
        product_variants: summary.submitEligible.map(item => item.id)
      });

      if (!this._isMounted) return;

      this.setState({ isSubmitting: false });
      this.props.onSuccess();
      this.props.toggle();
    } catch (error) {
      this._handleRequestError(error);

      if (!this._isMounted) return;

      this.setState({
        isSubmitting: false,
        submitError: 'Failed to replace collection product variants.'
      });
    }
  }

  _handleSubmit = () => {
    if (this.state.mode === MODE_REPLACE_ALL) {
      this.setState({ isReplaceConfirmationOpen: true });
      return;
    }

    this._submitBulkAdd();
  }

  render() {
    const summary = this._getSummary();
    const submitLabel = this.state.mode === MODE_REPLACE_ALL ? 'Replace All' : 'Bulk Add';
    const shouldShowSelectedSection = summary.valid.length > 0 || summary.alreadyExists.length > 0;

    return (
      <React.Fragment>
        <Modal isOpen={this.props.isOpen} toggle={this.props.toggle} size="lg">
          <ModalHeader toggle={this.props.toggle}>Bulk Product Variants</ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>
                SKU
                <button
                  type="button"
                  style={CSV_BTN_STYLE}
                  aria-label="Upload CSV (one SKU per row)"
                  onClick={this._triggerCsvInput}
                  disabled={this.state.csvParsing || this.state.csvValidating || this.state.isSubmitting}
                >
                  <i className="fa fa-upload" />
                </button>
              </Label>
              <input
                type="file"
                accept=".csv"
                ref={this.csvInputRef}
                style={HIDDEN_INPUT_STYLE}
                onChange={this._handleCsvFileChange}
              />
              <Select.Async
                multi
                valueKey="id"
                labelKey="name"
                value={this.state.stagedVariants}
                loadOptions={this._loadVariantOptions}
                onChange={this._handleStageChange}
                placeholder="Type and select SKU..."
                noResultsText="Cannot find product variant."
              />
              <small className="text-muted">
                Search keeps using the current Product Variants source and excludes SKUs that are already attached.
              </small>
            </FormGroup>

            <FormGroup>
              <Label>Mode</Label>
              <Input type="select" value={this.state.mode} onChange={this._handleModeChange}>
                <option value={MODE_BULK_ADD}>Bulk Add</option>
                <option value={MODE_REPLACE_ALL}>Replace All</option>
              </Input>
              {this.state.mode === MODE_REPLACE_ALL && (
                <small className="text-danger">
                  Replace All will remove current collection Product Variants and replace them with the staged valid SKU set only.
                </small>
              )}
            </FormGroup>

            {(this.state.csvParsing || this.state.csvValidating) && (
              <div className="mt-2">
                {this.state.csvParsing && (
                  <div className="d-flex align-items-center">
                    <Spinner size="sm" className="mr-1" />
                    <small>Parsing CSV...</small>
                  </div>
                )}
                {this.state.csvValidating && (
                  <div className="d-flex align-items-center">
                    <Spinner size="sm" className="mr-1" />
                    <small>Checking {this.state.csvValidationCount} SKU{this.state.csvValidationCount > 1 ? 's' : ''}...</small>
                  </div>
                )}
              </div>
            )}

            {this.state.showCsvResults && this.state.csvValidationResults && (
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <strong>CSV Review</strong><br />
                    <small className="text-muted">Review the upload result before adding it into the final selection.</small>
                  </div>
                  <div>
                    {(this.state.csvValidationResults.valid.length > 0 || this.state.csvValidationResults.alreadyExists.length > 0) && (
                      <Button color="primary" size="sm" onClick={this._confirmAddCsvResults} className="mr-1">
                        Add Results
                      </Button>
                    )}
                    <Button color="secondary" size="sm" onClick={this._clearCsvResults}>Cancel</Button>
                  </div>
                </div>

                {this._renderCsvReviewSection(
                  'Valid SKU',
                  'success',
                  this.state.csvValidationResults.valid,
                  item => (
                    <li key={`valid-${normalizeSku(item.sku)}`}>
                      {item.sku}
                      {item.display_name ? ` - ${item.display_name}` : ''}
                    </li>
                  )
                )}

                {this._renderCsvReviewSection(
                  'Already in Collection',
                  'warning',
                  this.state.csvValidationResults.alreadyExists,
                  item => (
                    <li key={`exists-${normalizeSku(item.sku)}`}>{item.sku}</li>
                  )
                )}

                {this._renderCsvReviewSection(
                  'Invalid SKU',
                  'danger',
                  this.state.csvValidationResults.invalid,
                  (item, index) => (
                    <li key={`${item.sku}-${index}`}>{item.sku} - {item.reason}</li>
                  )
                )}
              </div>
            )}

            {this.state.csvSuccessMessage && (
              <Alert color="success" className="mt-2">{this.state.csvSuccessMessage}</Alert>
            )}

            {this.state.csvError && (
              <Alert color="danger" className="mt-2">{this.state.csvError}</Alert>
            )}

            {this.state.selectionError && (
              <Alert color="danger" className="mt-2">{this.state.selectionError}</Alert>
            )}

            {this.state.submitError && (
              <Alert color="danger" className="mt-2">{this.state.submitError}</Alert>
            )}

            {shouldShowSelectedSection && (
              <div className="mt-3" style={SELECTED_BOX_STYLE}>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <strong>Selected SKU</strong><br />
                    <small className="text-muted">
                      This is the final SKU selection that will be used for {submitLabel.toLowerCase()}.
                    </small>
                  </div>
                  <div className="text-right" style={SMALL_TEXT_STYLE}>
                    <div><span className="text-success">{summary.valid.length}</span> valid new</div>
                    <div><span className="text-warning">{summary.alreadyExists.length}</span> already in collection</div>
                    <div><strong>{summary.submitEligible.length}</strong> eligible for {submitLabel}</div>
                  </div>
                </div>

                <div className="mt-3" style={SCROLLABLE_LIST_STYLE}>
                  <ul className="mb-0 pl-3" style={SMALL_TEXT_STYLE}>
                    {this.state.stagedVariants.map(item => (
                      <li key={`selected-${normalizeSku(item.sku)}`} className="mb-1">
                        <span>
                          {item.sku}
                          {item.display_name ? ` - ${item.display_name}` : ''}
                          {item.alreadyExists ? ' (already in collection)' : ''}
                        </span>
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-danger p-0 ml-2 align-baseline"
                          onClick={() => this._handleRemoveSelectedSku(item.sku)}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={this.props.toggle} disabled={this.state.isSubmitting}>Cancel</Button>
            <Button
              color="primary"
              onClick={this._handleSubmit}
              disabled={this.state.isSubmitting || summary.submitEligible.length === 0}
            >
              {this.state.isSubmitting ? 'Submitting...' : submitLabel}
            </Button>
          </ModalFooter>
        </Modal>

        <ModalConfirmation
          isOpen={this.state.isReplaceConfirmationOpen}
          toggle={() => this.setState({ isReplaceConfirmationOpen: false })}
          primaryText="Replace All"
          secondaryText="Cancel"
          modalBody="Current collection Product Variants will be removed and replaced with the staged valid SKU set only. Continue?"
          onSubmit={this._submitReplaceAll}
        />
      </React.Fragment>
    );
  }
}

export { ModalBulkProductVariant };
